import {
  STORAGE_KEY_ENUM,
  STATUS_TYPE_ENUM,
  EFFORT_MAIL_CONTACT_TYPE_ENUM,
  PROJECT_STEP_PRIORITY_TYPE_ENUM,
  getEffortMailContactTypeTranslated,
} from 'enums';
import {
  StyledBox,
  EditToolbar,
  ZebraDataGrid,
  RenderEditItem,
  CustomInputLabel,
  GridEditDateCell,
  GridFilterDateInput,
  ProductsCreateTable,
  RelatedContactsCreateTable,
} from 'components';
import {
  Alert,
  Radio,
  Button,
  Collapse,
  Backdrop,
  useTheme,
  TextField,
  FormLabel,
  Typography,
  IconButton,
  RadioGroup,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import {
  GridRowId,
  GridColDef,
  GridRowModes,
  GridRowsProp,
  GridRowModel,
  GridColTypeDef,
  GridEventListener,
  GridRowModesModel,
  GRID_DATE_COL_DEF,
  GridActionsCellItem,
  getGridDateOperators,
  GridRowEditStopReasons,
  GridPreProcessEditCellProps,
} from '@mui/x-data-grid-premium';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
  selectConnectionTypes,
  selectConnectionTypesIsEmpty,
  selectConnectionTypesLoading,
} from 'store/features/connection-types/selector';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import gibTypesActions from 'store/features/gib-types/actions';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import usedBanksActions from 'store/features/used-banks/actions';
import { ConnectionType, GibType, UsedBank, User } from 'library/types';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import connectionTypesActions from 'store/features/connection-types/actions';
import environmentTypesActions from 'store/features/environment-types/actions';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { selectGibTypes, selectGibTypesIsEmpty, selectGibTypesLoading } from 'store/features/gib-types/selector';
import { LocalStorage, renderFormattedDate, renderDataGridLocaleText, renderAutoCompleteLocaleText } from 'utils';
import { selectEnvironmentTypes, selectEnvironmentTypesIsEmpty } from 'store/features/environment-types/selector';
import { selectUsedBanks, selectUsedBanksIsEmpty, selectUsedBanksLoading } from 'store/features/used-banks/selector';

const language = LocalStorage.getItem(STORAGE_KEY_ENUM.LANGUAGE) || 'tr';

export type CompanyToBeCreated = {
  code: string;
  vkn: string;
  title: string;
  dateOfAnnulment: Dayjs | string | null;
};

export type RelatedContactToBeCreated = {
  nameSurname: string;
  email: string;
  phoneNumber: string;
};

export type ProductToBeCreated = {
  id: number;
  environmentTypeId: number | null;
  liveDate: Dayjs | string | null;
  isOnHold: boolean;
  priority: PROJECT_STEP_PRIORITY_TYPE_ENUM;
  relatedContactId: number | null;
  alternativeRelatedContactId: number | null;
  notes: string;
};

export type BookToBeCreated = {
  companyName: string | null;
  projects: ProductToBeCreated[];
  connectTypeId: number | null;
  gibTypeId: number | null;
  usedBankIds: number[];
  companies: CompanyToBeCreated[];
  apiInformation: string | null;
  vpnInformation: string | null;
  serviceApiInformation: string | null;
  rfcUserInformation: string | null;
  serverConnectionInformation: string | null;
  serverExternalIp: string | null;
  financialSeal: string | null;
  sapDev: string | null;
  sapTest: string | null;
  sapProd: string | null;
  note: string | null;
  relatedContacts: RelatedContactToBeCreated[];
  effortMailContactType: EFFORT_MAIL_CONTACT_TYPE_ENUM;
};

const initialState: BookToBeCreated = {
  companyName: '',
  projects: [],
  connectTypeId: null,
  gibTypeId: null,
  usedBankIds: [],
  companies: [],
  apiInformation: '',
  vpnInformation: '',
  serviceApiInformation: '',
  rfcUserInformation: '',
  serverConnectionInformation: '',
  serverExternalIp: '',
  financialSeal: '',
  sapDev: '',
  sapTest: '',
  sapProd: '',
  note: '',
  relatedContacts: [],
  effortMailContactType: EFFORT_MAIL_CONTACT_TYPE_ENUM.RELATED_CONTACTS_AND_REQUESTER,
};

const initialRows: GridRowsProp = [
  {
    id: crypto.randomUUID(),
    code: language === 'tr' ? 'Kod' : 'Code',
    vkn: language === 'tr' ? 'Vkn' : 'Tax Id',
    title: language === 'tr' ? 'Ünvan' : 'Title',
    dateOfAnnulment: null,
  },
];

const initialRowsRelatedContacts: GridRowsProp = [
  {
    id: crypto.randomUUID(),
    nameSurname: language === 'tr' ? 'AdSoyad' : 'NameSurname',
    email: language === 'tr' ? 'E-Posta' : 'E-Mail',
    phoneNumber: language === 'tr' ? 'Telefon' : 'Phone',
  },
];

const initialRowsProducts: GridRowsProp = [
  {
    id: crypto.randomUUID(),
    projectId: '',
    environmentTypeId: null,
    liveDate: null,
    isOnHold: false,
    priority: PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
    relatedContactId: null,
    alternativeRelatedContactId: null,
    notes: '',
  },
];

const dateColumnType: GridColTypeDef<Date, string> = {
  ...GRID_DATE_COL_DEF,
  resizable: false,
  renderEditCell: (params) => {
    return <GridEditDateCell {...params} />;
  },
  filterOperators: getGridDateOperators(false).map((item) => ({
    ...item,
    InputComponent: GridFilterDateInput,
    InputComponentProps: { showTime: false },
  })),
  valueFormatter: (value) => {
    if (value) {
      return renderFormattedDate(value);
    }

    return '';
  },
};

const CreateBook = () => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();

  const permissionCodes = useAppSelector(selectPermissionCodes);

  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);

  const connectionTypes = useAppSelector(selectConnectionTypes);
  const loadingConnectionTypes = useAppSelector(selectConnectionTypesLoading);
  const isEmptyConnectionTypes = useAppSelector(selectConnectionTypesIsEmpty);

  const environmentTypes = useAppSelector(selectEnvironmentTypes);
  const isEmptyEnvironmentTypes = useAppSelector(selectEnvironmentTypesIsEmpty);

  const gibTypes = useAppSelector(selectGibTypes);
  const loadingGibTypes = useAppSelector(selectGibTypesLoading);
  const isEmptyGibTypes = useAppSelector(selectGibTypesIsEmpty);

  const usedBanks = useAppSelector(selectUsedBanks);
  const loadingUsedBanks = useAppSelector(selectUsedBanksLoading);
  const isEmptyUsedBanks = useAppSelector(selectUsedBanksIsEmpty);

  // useState hooks
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState(initialRows);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [rowsProducts, setRowsProducts] = useState(initialRowsProducts);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [selectedUsedBanks, setSelectedUsedBanks] = useState<UsedBank[]>([]);
  const [selectedGibType, setSelectedGibType] = useState<ConnectionType | null>(null);
  const [rowModesModelProducts, setRowModesModelProducts] = useState<GridRowModesModel>({});
  const [rowsRelatedContacts, setRowsRelatedContacts] = useState(initialRowsRelatedContacts);
  const [selectedConnectionType, setSelectedConnectionType] = useState<ConnectionType | null>(null);
  const [rowModesModelRelatedContacts, setRowModesModelRelatedContacts] = useState<GridRowModesModel>({});

  // dynamic variables
  const incompleteAdaptation =
    isEmptyProducts || isEmptyConnectionTypes || isEmptyGibTypes || isEmptyUsedBanks || isEmptyEnvironmentTypes;

  const columns: GridColDef[] = [
    {
      field: 'order',
      headerName: intl.formatMessage({ id: 'project.table.order', defaultMessage: 'Sıra No' }),
      flex: 0.25,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      editable: false,
    },
    {
      field: 'code',
      headerName: intl.formatMessage({ id: 'project.table.code', defaultMessage: 'Şirket Kodu' }),
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfCompanies',
              defaultMessage: 'Şirket bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'vkn',
      headerName: intl.formatMessage({ id: 'project.table.vkn', defaultMessage: 'Vkn' }),
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfCompanies',
              defaultMessage: 'Şirket bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'title',
      headerName: intl.formatMessage({ id: 'project.table.title', defaultMessage: 'Ünvan' }),
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfCompanies',
              defaultMessage: 'Şirket bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'dateOfAnnulment',
      ...dateColumnType,
      headerName: intl.formatMessage({ id: 'project.table.dateOfAnnulment', defaultMessage: 'Fesih Tarihi' }),
      flex: 0.5,
      editable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: intl.formatMessage({ id: 'project.table.actions', defaultMessage: 'İşlemler' }),
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label={intl.formatMessage({ id: 'project.table.save', defaultMessage: 'Kaydet' })}
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label={intl.formatMessage({ id: 'project.table.cancel', defaultMessage: 'Vazgeç' })}
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label={intl.formatMessage({ id: 'project.table.edit', defaultMessage: 'Düzenle' })}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={intl.formatMessage({ id: 'project.table.delete', defaultMessage: 'Sil' })}
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  // form state
  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<BookToBeCreated>({
    defaultValues: initialState,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });

  // dynamic variables
  const watchedEffortMailContactType = watch('effortMailContactType');

  // async helper functions
  const onSubmit = async (data: BookToBeCreated) => {
    // Empty Company Name control
    if (!data.companyName)
      return enqueueSnackbar(
        <FormattedMessage id="project.requiredCompanyCode" defaultMessage="Firma boş bırakılamaz!" />,
        {
          variant: 'warning',
        }
      );

    // DataGrid Edit Mode Control For Companies
    const isViewModeForCompanies = Object.keys(rowModesModel).length === 0;

    if (!isViewModeForCompanies)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredDataGridViewModeForCompanies"
          defaultMessage="Şirketler tablosunda kaydedilmemiş satır bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Empty field of companies model control
    if (data.companies.some((company) => !company.code || !company.vkn || !company.title))
      return enqueueSnackbar(
        <FormattedMessage id="project.requiredFieldsOfCompanies" defaultMessage="Şirket bilgileri boş bırakılamaz!" />,
        {
          variant: 'warning',
        }
      );

    // At least one element should be existed in companies array control
    if (!data.companies.length)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredAtLeastOneElementOfCompanies"
          defaultMessage="En az bir şirket tanımlanmalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // DataGrid Edit Mode Control For Related Contacts
    const isViewModeForRelatedContacts = Object.keys(rowModesModelRelatedContacts).length === 0;

    if (!isViewModeForRelatedContacts)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredDataGridViewModeForRelatedContacts"
          defaultMessage="İlgili Kişiler tablosunda kaydedilmemiş satır bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Empty field of related contacts model control
    if (data.relatedContacts.some((relatedContact) => !relatedContact.nameSurname || !relatedContact.email))
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredFieldsOfRelatedContacts"
          defaultMessage="İlgili Kişilerin bilgileri boş bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Email format validation for related contacts
    const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
    if (data.relatedContacts.some((relatedContact) => relatedContact.email !== relatedContact.email.trim()))
      return enqueueSnackbar(
        <FormattedMessage
          id="validations.emailNoWhitespace"
          defaultMessage="E-posta adresinin başında veya sonunda boşluk olamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    if (data.relatedContacts.some((relatedContact) => /[A-Z]/.test(relatedContact.email)))
      return enqueueSnackbar(
        <FormattedMessage id="validations.emailNoUpperCase" defaultMessage="E-posta adresi büyük harf içeremez!" />,
        {
          variant: 'warning',
        }
      );

    if (data.relatedContacts.some((relatedContact) => /[çÇğĞıİöÖşŞüÜ]/.test(relatedContact.email)))
      return enqueueSnackbar(
        <FormattedMessage
          id="validations.emailNoTurkishChars"
          defaultMessage="E-posta adresi Türkçe karakter içeremez!"
        />,
        {
          variant: 'warning',
        }
      );

    if (data.relatedContacts.some((relatedContact) => !emailRegex.test(relatedContact.email)))
      return enqueueSnackbar(
        <FormattedMessage id="validations.invalidEmailFormat" defaultMessage="Geçersiz e-posta formatı" />,
        {
          variant: 'warning',
        }
      );

    // At least one element should be existed in related contacts array if effort contact type is only related contacts control
    if (
      watchedEffortMailContactType === EFFORT_MAIL_CONTACT_TYPE_ENUM.ONLY_RELATED_CONTACTS &&
      !data.relatedContacts.length
    )
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredAtLeastOneElementOfRelatedContactsIfEffortContactTypeIsOnlyRelatedContacts"
          defaultMessage="Sadece İlgili Kişiler seçili iken en az bir ilgili kişi tanımlanmalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // DataGrid Edit Mode Control For Products
    const isViewModeForProducts = Object.keys(rowModesModelProducts).length === 0;

    if (!isViewModeForProducts)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredDataGridViewModeForProducts"
          defaultMessage="Ürünler tablosunda kaydedilmemiş satır bırakılamaz!"
        />,
        {
          variant: 'warning',
        }
      );

    // Empty field of products model control
    if (data.projects.some((project) => !project.id))
      return enqueueSnackbar(
        <FormattedMessage id="project.requiredFieldsOfProducts" defaultMessage="Ürün bilgileri boş bırakılamaz!" />,
        {
          variant: 'warning',
        }
      );

    // At least one element should be existed in products array control
    if (!data.projects.length)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.requiredAtLeastOneElementOfProjects"
          defaultMessage="En az bir ürün tanımlanmalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // Check that there is no more than one of the same product
    const projectIds = data.projects.map((project) => project.id);
    if (projectIds.filter((item, index) => projectIds.indexOf(item) !== index).length)
      return enqueueSnackbar(
        <FormattedMessage
          id="project.shouldNotBeMoreThanOneOfTheSameProduct"
          defaultMessage="Aynı üründen birden fazla olmamalıdır!"
        />,
        {
          variant: 'warning',
        }
      );

    // Convert empty string values to null
    const realData: BookToBeCreated = Object.entries(data).reduce((acc, arrEl) => {
      if (arrEl[1] === '') return { ...acc, [arrEl[0]]: null };

      return { ...acc, [arrEl[0]]: arrEl[1] };
    }, initialState);

    // continue
    try {
      setLoading(true);

      const response = await apiService.createBook(realData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage id="project.createSuccess" defaultMessage="Proje başarılı bir şekilde oluşturuldu." />,
          {
            variant: 'success',
          }
        );

        goToMainPage();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const resetForm = () => {
    setSelectedConnectionType(null);
    setSelectedGibType(null);
    setSelectedUsedBanks([]);
    setRows(initialRows);
    setRowModesModel({});
    setRowsProducts(initialRowsProducts);
    setRowModesModelProducts({});
    setRowsRelatedContacts(initialRowsRelatedContacts);
    setRowModesModelRelatedContacts({});

    reset();
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const goToMainPage = useCallback(() => navigate('/'), [navigate]);

  const hideAlertBox = useCallback(() => setOpen(false), []);

  const onPressTabKeyHandler = (e: any) => {
    const { value } = e.target;

    if (e.key === 'Tab') {
      e.preventDefault();

      const cursorPosition = e.target.selectionStart;
      const cursorEndPosition = e.target.selectionEnd;
      const tab = '\t';

      const lastValue = value.substring(0, cursorPosition) + tab + value.substring(cursorEndPosition);

      e.target.value = lastValue;
      setValue(e.target.id, lastValue);

      // if you modify the value programmatically, the cursor is moved
      // to the end of the value, we need to reset it to the correct
      // position again
      e.target.selectionStart = cursorPosition + 1;
      e.target.selectionEnd = cursorPosition + 1;
    }
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('create-book')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    const getUsers = async () => {
      try {
        const response = await apiService.getUsers();
        if (response.isSuccess) {
          const formattedData = response.data
            .filter((user) => user.status === STATUS_TYPE_ENUM.ACTIVE)
            .sort((a, b) => (a.nameSurname || '').localeCompare(b.nameSurname || ''));
          setUsers(formattedData);
        }
      } catch {}
    };

    getUsers();
  }, []);

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    if (!connectionTypes.length && !isEmptyConnectionTypes) {
      dispatch(connectionTypesActions.getConnectionTypes());
    }
  }, [connectionTypes, isEmptyConnectionTypes, dispatch]);

  useEffect(() => {
    if (!environmentTypes.length && !isEmptyEnvironmentTypes) {
      dispatch(environmentTypesActions.getEnvironmentTypes());
    }
  }, [environmentTypes, isEmptyEnvironmentTypes, dispatch]);

  useEffect(() => {
    if (!gibTypes.length && !isEmptyGibTypes) {
      dispatch(gibTypesActions.getGibTypes());
    }
  }, [gibTypes, isEmptyGibTypes, dispatch]);

  useEffect(() => {
    if (!usedBanks.length && !isEmptyUsedBanks) {
      dispatch(usedBanksActions.getUsedBanks());
    }
  }, [usedBanks, isEmptyUsedBanks, dispatch]);

  useEffect(() => {
    const formattedCompanies: CompanyToBeCreated[] = rows.map((row) => ({
      code: row.code,
      vkn: row.vkn,
      title: row.title,
      dateOfAnnulment: row.dateOfAnnulment ? dayjs(row.dateOfAnnulment).toISOString() : null,
    }));

    setValue('companies', formattedCompanies);
  }, [rows, setValue]);

  useEffect(() => {
    const formattedRelatedContacts: RelatedContactToBeCreated[] = rowsRelatedContacts.map((row) => ({
      nameSurname: row.nameSurname,
      email: row.email,
      phoneNumber: row.phoneNumber,
    }));

    setValue('relatedContacts', formattedRelatedContacts);
  }, [rowsRelatedContacts, setValue]);

  useEffect(() => {
    const formattedProducts: ProductToBeCreated[] = rowsProducts.map((row) => ({
      id: products.find((product) => product.title === row.projectId)?.id || 0,
      environmentTypeId:
        environmentTypes.find((environmentType) => environmentType.title === row.environmentTypeId)?.id || null,
      liveDate: row.liveDate ? dayjs(row.liveDate).toISOString() : null,
      isOnHold: row.isOnHold || false,
      priority: row.priority || PROJECT_STEP_PRIORITY_TYPE_ENUM.NON_URGENT,
      relatedContactId: row.relatedContactId || null,
      alternativeRelatedContactId: row.alternativeRelatedContactId || null,
      notes: row.notes || '',
    }));

    setValue('projects', formattedProducts);
  }, [rowsProducts, environmentTypes, products, setValue]);

  return incompleteAdaptation ? (
    <Alert severity="info" icon={false}>
      <Typography>
        <FormattedMessage
          id="general.completeAdaptationsText"
          defaultMessage="Sayfayı görüntülemek için lütfen uyarlamaları tamamlayınız..! (Ürün Grupları, Ürünler, Bağlantı Türleri, Sunucu Tipleri, GİB Yöntemleri, Kullanılan Bankalar)"
        />
      </Typography>
    </Alert>
  ) : (
    <Grid container direction="column" gap={3}>
      <Grid size={{ xs: 12 }}>
        <Collapse in={open}>
          <Alert
            severity="info"
            icon={false}
            action={
              <IconButton
                sx={{ border: 'none' }}
                aria-label="close"
                color="inherit"
                size="small"
                onClick={hideAlertBox}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }>
            <Typography>
              <FormattedMessage
                id="project.titleText"
                defaultMessage="Aşağıdaki formu doldurarak yeni bir proje oluşturabilirsiniz."
              />
            </Typography>
          </Alert>
        </Collapse>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.companyName" defaultMessage="Firma" />
        </FormLabel>
        <Controller
          name="companyName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.companyName}
              helperText={errors.companyName?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
        <CustomInputLabel>
          <FormattedMessage id="project.companies" defaultMessage="Şirketler" />
        </CustomInputLabel>
        <StyledBox>
          <ZebraDataGrid
            rows={rows}
            columns={columns}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            localeText={renderDataGridLocaleText()}
            slots={{ toolbar: EditToolbar }}
            slotProps={{
              toolbar: { setRows, setRowModesModel },
            }}
            hideFooter
            disableColumnMenu
            disableColumnSorting
            disableRowSelectionOnClick
            sx={{ border: `1px ${theme.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid` }}
            disableAggregation
            disableRowGrouping
          />
        </StyledBox>
      </Grid>
      <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
        <CustomInputLabel>
          <FormattedMessage id="project.relatedContacts" defaultMessage="İlgili Kişiler" />
        </CustomInputLabel>
        <Controller
          name="effortMailContactType"
          control={control}
          render={({ field }) => (
            <RadioGroup
              sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}
              row
              value={field.value}
              onChange={(e) => field.onChange(Number(e.target.value))}>
              <Typography mr={2}>
                <FormattedMessage
                  id="project.effortMailContactTypeLabel"
                  defaultMessage="Efor Sürecinde E-Posta Kime Gitsin?"
                />
              </Typography>
              <FormControlLabel
                value={EFFORT_MAIL_CONTACT_TYPE_ENUM.ONLY_RELATED_CONTACTS}
                control={<Radio />}
                label={getEffortMailContactTypeTranslated(EFFORT_MAIL_CONTACT_TYPE_ENUM.ONLY_RELATED_CONTACTS)}
              />
              <FormControlLabel
                value={EFFORT_MAIL_CONTACT_TYPE_ENUM.RELATED_CONTACTS_AND_REQUESTER}
                control={<Radio />}
                label={getEffortMailContactTypeTranslated(EFFORT_MAIL_CONTACT_TYPE_ENUM.RELATED_CONTACTS_AND_REQUESTER)}
              />
            </RadioGroup>
          )}
        />
        <RelatedContactsCreateTable
          rows={rowsRelatedContacts}
          setRows={setRowsRelatedContacts}
          rowModesModel={rowModesModelRelatedContacts}
          setRowModesModel={setRowModesModelRelatedContacts}
        />
      </Grid>
      <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
        <CustomInputLabel>
          <FormattedMessage id="project.project" defaultMessage="Ürünler" />
        </CustomInputLabel>
        <ProductsCreateTable
          rows={rowsProducts}
          setRows={setRowsProducts}
          rowModesModel={rowModesModelProducts}
          setRowModesModel={setRowModesModelProducts}
          products={products}
          environmentTypes={environmentTypes}
          users={users}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.connectType" defaultMessage="Bağlantı Türü" />
        </FormLabel>
        <Autocomplete
          size="small"
          options={connectionTypes}
          loading={loadingConnectionTypes}
          value={selectedConnectionType}
          {...renderAutoCompleteLocaleText()}
          onChange={(_, newValue: ConnectionType | null) => {
            setSelectedConnectionType(newValue);

            setValue('connectTypeId', newValue ? newValue.id : null);
          }}
          getOptionLabel={(option: ConnectionType) => option.title}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                '& .MuiButtonBase-root': {
                  border: 'none',
                  backgroundColor: 'inherit',
                  width: '2.25rem',
                  height: '2.25rem',
                  padding: '0.25rem',
                  [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                  },
                },
              }}
              variant="outlined"
              fullWidth
              size="small"
              error={!!errors.connectTypeId}
              helperText={errors.connectTypeId?.message}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingConnectionTypes ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.gibType" defaultMessage="GİB Yöntemi" />
        </FormLabel>
        <Autocomplete
          size="small"
          options={gibTypes}
          loading={loadingGibTypes}
          value={selectedGibType}
          {...renderAutoCompleteLocaleText()}
          onChange={(_, newValue: GibType | null) => {
            setSelectedGibType(newValue);

            setValue('gibTypeId', newValue ? newValue.id : null);
          }}
          getOptionLabel={(option: GibType) => option.title}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                '& .MuiButtonBase-root': {
                  border: 'none',
                  backgroundColor: 'inherit',
                  width: '2.25rem',
                  height: '2.25rem',
                  padding: '0.25rem',
                  [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                  },
                },
              }}
              variant="outlined"
              fullWidth
              size="small"
              error={!!errors.gibTypeId}
              helperText={errors.gibTypeId?.message}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingGibTypes ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.usedBanks" defaultMessage="Kullanılan Bankalar" />
          &nbsp;
          {`${
            selectedUsedBanks.length ? `( ${selectedUsedBanks.length} ${intl.locale === 'tr' ? 'Banka' : 'Bank'} )` : ''
          }`}
        </FormLabel>
        <Autocomplete
          multiple
          filterSelectedOptions
          size="small"
          options={usedBanks}
          loading={loadingUsedBanks}
          disableCloseOnSelect
          value={selectedUsedBanks}
          {...renderAutoCompleteLocaleText()}
          onChange={(_, newValue: UsedBank[]) => {
            setSelectedUsedBanks(newValue);

            const usedBankIds = newValue.map((value) => value.id);
            setValue('usedBankIds', usedBankIds);
          }}
          getOptionLabel={(option: UsedBank) => option.title}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                '& .MuiButtonBase-root': {
                  border: 'none',
                  width: 'fit-content',
                  height: '2.25rem',
                  padding: '0.25rem',
                  [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                  },
                },
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              multiline
              variant="outlined"
              fullWidth
              size="small"
              error={!!errors.usedBankIds}
              helperText={errors.usedBankIds?.message}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsedBanks ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.apiInformation" defaultMessage="API Şifresi" />
        </FormLabel>
        <Controller
          name="apiInformation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.apiInformation}
              helperText={errors.apiInformation?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.serviceApiInformation" defaultMessage="Sunucu API Bilgileri" />
        </FormLabel>
        <Controller
          name="serviceApiInformation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.serviceApiInformation}
              helperText={errors.serviceApiInformation?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.rfcUserInformation" defaultMessage="RFC Kullanıcı Bilgileri" />
        </FormLabel>
        <Controller
          name="rfcUserInformation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.rfcUserInformation}
              helperText={errors.rfcUserInformation?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.vpnInformation" defaultMessage="VPN Bilgileri" />
        </FormLabel>
        <Controller
          name="vpnInformation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.vpnInformation}
              helperText={errors.vpnInformation?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.serverConnectionInformation" defaultMessage="Sunucu Bağlantı Bilgileri" />
        </FormLabel>
        <Controller
          name="serverConnectionInformation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.serverConnectionInformation}
              helperText={errors.serverConnectionInformation?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.serverExternalIp" defaultMessage="Sunucu Dış IP Bilgisi" />
        </FormLabel>
        <Controller
          name="serverExternalIp"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.serverExternalIp}
              helperText={errors.serverExternalIp?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.financialSeal" defaultMessage="Mali Mühür" />
        </FormLabel>
        <Controller
          name="financialSeal"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.financialSeal}
              helperText={errors.financialSeal?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.sapDev" defaultMessage="SAP Dev" />
        </FormLabel>
        <Controller
          name="sapDev"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.sapDev}
              helperText={errors.sapDev?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.sapTest" defaultMessage="SAP QA" />
        </FormLabel>
        <Controller
          name="sapTest"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.sapTest}
              helperText={errors.sapTest?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.sapProd" defaultMessage="SAP Canlı" />
        </FormLabel>
        <Controller
          name="sapProd"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.sapProd}
              helperText={errors.sapProd?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormLabel>
          <FormattedMessage id="project.note" defaultMessage="Notlar" />
        </FormLabel>
        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              fullWidth
              size="small"
              onKeyDown={onPressTabKeyHandler}
              multiline
              sx={{
                '& .MuiInputBase-root': {
                  height: 'auto',
                },
              }}
              error={!!errors.note}
              helperText={errors.note?.message}
            />
          )}
        />
      </Grid>
      <Grid container direction="row" gap={2}>
        <Grid>
          <Button onClick={handleSubmit(onSubmit)} variant="contained" sx={{ mb: 2 }}>
            <FormattedMessage id="project.create" defaultMessage="Oluştur" />
          </Button>
        </Grid>
        <Grid>
          <Button onClick={resetForm} variant="contained" color="inherit" sx={{ mb: 2 }}>
            <FormattedMessage id="project.reset" defaultMessage="Temizle" />
          </Button>
        </Grid>
      </Grid>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Grid>
  );
};

export default CreateBook;
